import React, { forwardRef, memo, useRef, useCallback } from 'react';
import { classNames } from '../../utils/classNames';
import { useSingleEdit, useEditPermission, useInputFocus } from '../../hooks';
import { CommentEditMode } from '../CommentEditMode/CommentEditMode';
import { CommentViewMode } from '../CommentViewMode/CommentViewMode';
import { CommentMetadata } from '../CommentMetadata/CommentMetadata';
import type { CommentTextProps } from './CommentText.types';
import './CommentText.css';
import '../CommentCard/CommentSkeleton.css';
import type {
  CommentMention,
  CommentAttachment
} from '../../containers/CommentList/CommentList.types';

const CommentTextComponent = forwardRef<HTMLDivElement, CommentTextProps>(
  (
    {
      text,
      commentMentions,
      createdAt,
      editedAt,
      section,
      onTextEdit,
      onEditStart,
      onEditComplete,
      onEditCancel,
      isEditing: controlledEditing,
      isEditSkeleton = false,
      authorEmail,
      currentUserEmail,

      styleOverrides,
      className,
      children,
      inputConfig,
      iconOverrides,
      showEditedAt,
      submitOnBlur = false,
      attachments,
      communicationMode,
      ...restProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const {
      isEditingActive,
      editText,
      setEditText,
      startEdit,
      finishEdit,
      cancelEdit
    } = useSingleEdit(
      text,
      controlledEditing,
      onEditStart,
      onEditComplete,
      onEditCancel
    );

    const canEdit = useEditPermission(
      onTextEdit,
      authorEmail,
      currentUserEmail
    );

    useInputFocus(isEditingActive, text, setEditText, inputRef);

    const handleContainerClick = useCallback(
      (event: React.MouseEvent) => {
        if (isEditingActive) {
          event.stopPropagation();
        }
      },
      [isEditingActive]
    );

    const handleSubmit = useCallback(
      (
        value: string,
        mentions?: CommentMention[],
        attachments?: (CommentAttachment | File)[]
      ) => {
        if (onTextEdit) {
          onTextEdit(value, mentions, attachments);
        }
        finishEdit(value);
      },
      [onTextEdit, finishEdit]
    );

    return (
      <div
        ref={ref}
        className={classNames(
          'comment-text',
          isEditSkeleton && 'comment-text--edit-skeleton',
          className
        )}
        style={styleOverrides}
        onClick={handleContainerClick}
        {...restProps}
      >
        {isEditingActive ? (
          <CommentEditMode
            ref={inputRef}
            editText={editText}
            onTextChange={setEditText}
            onSubmit={handleSubmit}
            onCancel={cancelEdit}
            initialMentions={commentMentions}
            initialAttachments={attachments}
            submitOnBlur={submitOnBlur}
            {...inputConfig}
          />
        ) : (
          <CommentViewMode
            text={text}
            canEdit={canEdit}
            onEditStart={startEdit}
            commentMentions={commentMentions}
            tooltipStyle={inputConfig?.mentionTooltipStyle}
            iconOverrides={iconOverrides}
            displayMode={inputConfig?.mentionTooltipDisplay}
            attachments={attachments}
          />
        )}{' '}
        {(!isEditingActive || submitOnBlur) && (
          <CommentMetadata
            createdAt={createdAt}
            editedAt={editedAt}
            section={section}
            showEditedAt={showEditedAt}
            communicationMode={communicationMode}
            iconOverrides={iconOverrides}
          />
        )}
        {children && <div className="comment-text__children">{children}</div>}
      </div>
    );
  }
);

CommentTextComponent.displayName = 'CommentText';

export const CommentText = memo(CommentTextComponent);
